import { useEffect, type FC, type ReactNode } from 'react'
import { StatsigProvider, useStatsigClient } from '@statsig/react-bindings'
import { observer } from 'mobx-react-lite'
import { useEntitiesContext } from 'entities/EntitiesExport'

const StatsigExperimentListener: FC = () => {
  const entities = useEntitiesContext()
  const { client } = useStatsigClient()

  useEffect(() => {
    const listener = (event: {
      experiment: { name: string; groupName: string | null; value: Record<string, unknown> }
    }) => {
      if (!event.experiment.groupName) return
      entities.eventLogStore.logEvent('Experiment Viewed', {
        experiment_id: event.experiment.name,
        variation_id: event.experiment.groupName,
        variation_name: event.experiment.value?.modal_version,
      })
    }

    client.on('experiment_evaluation', listener)

    return () => client.off('experiment_evaluation', listener)
  }, [client, entities.eventLogStore])

  return null
}

const toStatsigTier = (env: string | undefined): string => {
  if (env === 'local') return 'development'
  if (env === 'qa') return 'staging'
  return env ?? 'production'
}

type Props = { children: ReactNode }

export const StatsigAppProvider: FC<Props> = observer(({ children }) => {
  const { usersStore } = useEntitiesContext()
  const userId = usersStore.user?.id

  if (!userId) return <>{children}</>

  return (
    <StatsigProvider
      sdkKey={process.env.STATSIG_CLIENT_KEY ?? ''}
      user={{ userID: String(userId), custom: { surface: 'web_app' } }}
      options={{ environment: { tier: toStatsigTier(process.env.REACT_APP_ENV) } }}
    >
      <StatsigExperimentListener />
      {children}
    </StatsigProvider>
  )
})
